import { useCallback, useEffect, useState } from "react";

export type PortalDataState<TData> =
  | { status: "loading"; data?: undefined; error?: undefined }
  | { status: "success"; data: TData; error?: undefined }
  | { status: "refreshing"; data: TData; error?: undefined }
  | { status: "error"; data?: undefined; error: unknown };

export function usePortalData<TData>(
  request: (signal: AbortSignal) => Promise<TData>,
  options: { enabled?: boolean } = {},
): PortalDataState<TData> & { refresh: () => void } {
  const enabled = options.enabled ?? true;
  const [state, setState] = useState<PortalDataState<TData>>({
    status: "loading",
  });
  const [requestVersion, setRequestVersion] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    setState((current) =>
      current.status === "success" || current.status === "refreshing"
        ? { status: "refreshing", data: current.data }
        : { status: "loading" }
    );

    request(controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setState({ status: "success", data });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setState({ status: "error", error });
      });

    return () => controller.abort();
  }, [enabled, request, requestVersion]);

  const refresh = useCallback(() => setRequestVersion((value) => value + 1), []);
  return { ...state, refresh };
}
